import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_akruti-3d-build/artifacts/x72hpznj_Contact%20Us%20%281%29.jpg';
const WHATSAPP_NUMBER = '918880331224';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Projects', path: '/projects' },
  { name: 'Interiors', path: '/interiors' },
  { name: 'Materials', path: '/materials' },
  { name: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Check if we're on home page
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleConsultationClick = () => {
    const message = encodeURIComponent('Hello! I would like to get a free consultation for my construction/interior project.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };
  
  // Determine nav text color based on page and scroll state
  const getNavTextColor = (isActive) => {
    if (isActive) return 'text-[#C9A646]';
    if (isHomePage || isScrolled) return 'text-[#1a1a1a] hover:text-[#C9A646]';
    return 'text-white hover:text-[#C9A646]';
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#ececec]/95 backdrop-blur-md shadow-sm'
            : isHomePage ? 'bg-transparent' : 'bg-[#1a1a1a]'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Akruti Engineers & Builders" 
                className="h-12 md:h-14 w-auto object-contain rounded-lg"
                loading="eager"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs tracking-[0.15em] uppercase font-semibold transition-colors duration-200 ${getNavTextColor(location.pathname === link.path)}`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleConsultationClick}
                className="hidden md:flex btn-primary text-xs tracking-wider"
                data-testid="consultation-btn"
              >
                Get Free Consultation
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-900 hover:text-[#C9A646] transition-colors"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-40 bg-[#ececec]/98 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8 pt-20">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    className={`text-2xl font-heading tracking-wider ${
                      location.pathname === link.path
                        ? 'text-[#C9A646]'
                        : 'text-gray-900 hover:text-[#C9A646]'
                    } transition-colors`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                onClick={handleConsultationClick}
                className="btn-primary text-sm tracking-wider mt-8"
                data-testid="mobile-consultation-btn"
              >
                Get Free Consultation
              </motion.button>

              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                href="tel:+918880331224"
                className="flex items-center gap-2 text-[#C9A646] mt-4"
              >
                <Phone size={20} />
                <span>+91 88803 31224</span>
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
